import React, { useEffect, useRef, useState } from "react";
import { ArrowUpOutlined, DownloadOutlined } from "@ant-design/icons";

const COUNT_MS = 3200;
const HOLD_MS = 2200;
const CYCLE_MS = COUNT_MS + HOLD_MS;
const TARGET_OPENS = 128;
const TARGET_CLICKS = 42;

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

/**
 * A tiny looping demo of a campaign delivering: a progress bar counts up
 * to 100%, while opens and clicks tick up alongside it. Needs real
 * counting numbers, not just cross-fades, so this one is a small
 * requestAnimationFrame loop instead of pure CSS like its siblings.
 * prefers-reduced-motion skips the loop and shows the finished state.
 */
export default function DeliveryTrackingAnimation() {
  const [percent, setPercent] = useState(0);
  const [opens, setOpens] = useState(0);
  const [clicks, setClicks] = useState(0);
  const startRef = useRef(null);
  const frameRef = useRef(null);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReducedMotion) {
      setPercent(100);
      setOpens(TARGET_OPENS);
      setClicks(TARGET_CLICKS);
      return;
    }

    const tick = (timestamp) => {
      if (startRef.current === null) startRef.current = timestamp;
      const elapsed = (timestamp - startRef.current) % CYCLE_MS;
      if (elapsed <= COUNT_MS) {
        const t = easeOutCubic(elapsed / COUNT_MS);
        setPercent(Math.round(t * 100));
        setOpens(Math.round(t * TARGET_OPENS));
        setClicks(Math.round(t * TARGET_CLICKS));
      } else {
        setPercent(100);
        setOpens(TARGET_OPENS);
        setClicks(TARGET_CLICKS);
      }
      frameRef.current = requestAnimationFrame(tick);
    };
    frameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameRef.current);
  }, []);

  return (
    <div className="delivery-tracking-demo" aria-hidden="true">
      <div className="dtd-progress-header">
        <span className="dtd-progress-label">Delivering</span>
        <span className="dtd-progress-percent mk-mono">{percent}%</span>
      </div>
      <div className="dtd-progress-track">
        <div className="dtd-progress-fill" style={{ width: `${percent}%` }} />
      </div>

      <div className="dtd-stats">
        <div className="dtd-stat">
          <span className="dtd-stat-label mk-mono">Opens</span>
          <span className="dtd-stat-value">
            <ArrowUpOutlined className="dtd-stat-arrow" />
            {opens}
          </span>
        </div>
        <div className="dtd-stat">
          <span className="dtd-stat-label mk-mono">Clicks</span>
          <span className="dtd-stat-value">
            <ArrowUpOutlined className="dtd-stat-arrow" />
            {clicks}
          </span>
        </div>
      </div>

      <button type="button" className="dtd-download" tabIndex={-1}>
        <DownloadOutlined /> Download Report
      </button>
    </div>
  );
}
